/**
 * Gesture-safe handoff to a third-party hosted page (Stripe Checkout, the
 * Stripe Customer Portal).
 *
 * WHY THIS EXISTS
 * ---------------
 * Both destinations require a round-trip to an Edge Function first, to mint
 * a single-use session URL. Doing `window.open(url)` *after* that `await`
 * fails on iOS Safari: the popup allowance is tied to the user-gesture task,
 * and awaiting a promise ends it. The only reliable shape is to open a blank
 * tab SYNCHRONOUSLY inside the click handler and point it at the URL once the
 * URL resolves.
 *
 * The tab also removes the dependency on the browser Back button. Stripe
 * Checkout (and Link's "Confirm it's you" step in particular) is a hosted SPA
 * that manages its own history; once we hand the current tab over to it, the
 * only way back into Yaruta is through history we do not control. With a
 * separate tab the app is never lost — closing the Stripe tab returns to it.
 *
 * Popups can still be refused (a stricter blocker, an in-app webview). In
 * that case `blocked` is true and `navigate()` degrades to the previous
 * behaviour, a same-tab redirect, so the flow never dead-ends.
 *
 * The opened tab deliberately keeps its `opener` back-reference: we need the
 * handle to navigate and to close it, `rel=noopener` is not available to
 * `window.open` handles, and nulling `opener` afterwards can cost the
 * "back closes this tab and returns to the opener" behaviour that is half
 * the point. The destination is the payment provider itself, so the residual
 * reverse-tabnabbing surface is not a meaningful addition to the trust we
 * already extend it.
 */

/** The bits of the opened tab we touch. Structural so tests can stand one in. */
interface OpenedTab {
  location: { href: string };
  close(): void;
  document: { write(html: string): void; close(): void };
}

/** The bits of the hosting window we touch. */
export interface TabHost {
  open(url: string, target: string): OpenedTab | null;
  location: { assign(url: string): void };
}

export interface ExternalTab {
  /**
   * True when the browser refused the popup. `navigate()` will then fall
   * back to a same-tab redirect; callers should assume the current document
   * is about to be replaced.
   */
  readonly blocked: boolean;
  /** Point the tab at `url` (or redirect this tab if the popup was blocked). */
  navigate(url: string): void;
  /** Close the placeholder tab — call this when the session request fails. */
  cancel(): void;
}

/**
 * Minimal placeholder so the user isn't staring at a blank white tab for the
 * duration of the session request. Static markup only: nothing here is
 * derived from user input or from a server response.
 */
const PLACEHOLDER_HTML =
  '<!doctype html><html><head><meta charset="utf-8">' +
  '<meta name="viewport" content="width=device-width,initial-scale=1">' +
  '<title>Yaruta</title></head>' +
  '<body style="margin:0;display:flex;align-items:center;justify-content:center;' +
  "height:100vh;font:16px -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
  'color:#5e5e5e;background:#ffffff">Opening secure checkout&hellip;</body></html>';

/**
 * Open a blank tab NOW, before any `await`. Must be called synchronously from
 * within the user-gesture handler — that is the whole point.
 *
 * `host` exists so this is unit-testable: jsdom makes `window.location`
 * unforgeable, so a real navigation cannot be spied on. Production callers
 * pass nothing.
 */
export function openExternalTab(host: TabHost = window): ExternalTab {
  let handle: OpenedTab | null = null;
  try {
    handle = host.open('', '_blank');
  } catch {
    // Some embedded webviews throw rather than returning null.
    handle = null;
  }

  if (!handle) {
    return {
      blocked: true,
      navigate(url: string) {
        host.location.assign(url);
      },
      cancel() {
        /* nothing was opened */
      },
    };
  }

  const opened = handle;
  try {
    opened.document.write(PLACEHOLDER_HTML);
    opened.document.close();
  } catch {
    // Writing to the placeholder is cosmetic; never let it break the handoff.
  }

  return {
    blocked: false,
    navigate(url: string) {
      try {
        opened.location.href = url;
      } catch {
        // The tab was closed by the user while we were fetching the URL.
        // Falling back keeps a payment attempt from silently evaporating.
        host.location.assign(url);
      }
    },
    cancel() {
      try {
        opened.close();
      } catch {
        // Already gone.
      }
    },
  };
}
